import React, { useMemo } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import styled from 'styled-components';

interface GanttTimelineHeaderProps {
  endDate: Dayjs;
  startDate: Dayjs;
}

const GanttTimelineHeader: React.FC<GanttTimelineHeaderProps> = ({ endDate, startDate }) => {
  // Generate year markers for the timeline (cleaner approach)
  const yearMarkers = useMemo(() => {
    const markers: { end: Dayjs; start: Dayjs; year: number }[] = [];
    let currentYear = startDate.year();
    const endYear = endDate.year();

    while (currentYear <= endYear) {
      const yearStart = startDate.year(currentYear).startOf('year');
      const yearEnd = startDate.year(currentYear).endOf('year');

      // Clamp to the actual timeline bounds
      const actualStart = yearStart.isBefore(startDate) ? startDate : yearStart;
      const actualEnd = yearEnd.isAfter(endDate) ? endDate : yearEnd;

      markers.push({
        year: currentYear,
        start: actualStart,
        end: actualEnd,
      });

      currentYear += 1;
    }

    return markers;
  }, [startDate, endDate]);

  // Generate month markers for granular display
  const monthMarkers = useMemo(() => {
    const markers: { end: Dayjs; month: number; start: Dayjs; year: number }[] = [];
    let current = startDate.startOf('month');

    while (current.isBefore(endDate) || current.isSame(endDate, 'month')) {
      const monthEnd = current.endOf('month');
      const actualEnd = monthEnd.isAfter(endDate) ? endDate : monthEnd;

      markers.push({
        year: current.year(),
        month: current.month() + 1, // dayjs months are 0-indexed
        start: current,
        end: actualEnd,
      });

      current = current.add(1, 'month').startOf('month');
    }

    return markers;
  }, [startDate, endDate]);

  const totalDays = endDate.diff(startDate, 'days');

  // Today triangle logic
  const todayTrianglePosition = useMemo(() => {
    const today = dayjs();

    // Only show if today is within the timeline range
    if (today.isBefore(startDate) || today.isAfter(endDate)) {
      return null;
    }

    const daysSinceStart = today.diff(startDate, 'days');
    const leftPercent = (daysSinceStart / totalDays) * 100;
    return leftPercent;
  }, [startDate, endDate, totalDays]);

  return (
    <HeaderContainer>
      {/* Today triangle indicator */}
      {todayTrianglePosition !== null && <TodayTriangle left={todayTrianglePosition} />}

      {/* Year bands row */}
      <YearBandsContainer>
        {yearMarkers.map((yearData, index) => {
          const yearDays = yearData.end.diff(yearData.start, 'days') + 1;
          const widthPercent = (yearDays / totalDays) * 100;
          const startOffsetDays = yearData.start.diff(startDate, 'days');
          const leftPercent = (startOffsetDays / totalDays) * 100;

          return (
            <YearBand
              key={yearData.year}
              width={widthPercent}
              left={leftPercent}
              $isEven={index % 2 === 0}
            >
              <YearLabel>{yearData.year}</YearLabel>
            </YearBand>
          );
        })}
      </YearBandsContainer>

      {/* Month indicators row */}
      <MonthIndicatorsContainer>
        {monthMarkers.map(monthData => {
          const monthDays = monthData.end.diff(monthData.start, 'days') + 1;
          const widthPercent = (monthDays / totalDays) * 100;
          const startOffsetDays = monthData.start.diff(startDate, 'days');
          const leftPercent = (startOffsetDays / totalDays) * 100;

          // Center the month label in the middle of its time span
          const centerPercent = leftPercent + widthPercent / 2;

          return (
            <MonthIndicator
              key={`${monthData.year}-${monthData.month}`}
              centerPosition={centerPercent}
            >
              {dayjs()
                .month(monthData.month - 1)
                .format('MMM')
                .toUpperCase()}
            </MonthIndicator>
          );
        })}
      </MonthIndicatorsContainer>

      {/* Month boundary lines */}
      <MonthBoundaryLines>
        {monthMarkers.slice(1).map(monthData => {
          // Skip the line between December and January (when month is 1)
          if (monthData.month === 1) {
            return null;
          }

          const daysSinceStart = monthData.start.diff(startDate, 'days');
          const leftPercent = (daysSinceStart / totalDays) * 100;

          return (
            <MonthBoundaryLine key={`${monthData.year}-${monthData.month}`} left={leftPercent} />
          );
        })}
      </MonthBoundaryLines>

      {/* Year boundary lines */}
      <YearBoundaryLines>
        {yearMarkers.slice(1).map(yearData => {
          const daysSinceStart = yearData.start.diff(startDate, 'days');
          const leftPercent = (daysSinceStart / totalDays) * 100;

          return <YearBoundaryLine key={yearData.year} left={leftPercent} />;
        })}
      </YearBoundaryLines>
    </HeaderContainer>
  );
};

const HeaderContainer = styled.div`
  position: relative;
  height: 80px;
  width: 100%;
  border-bottom: 1px solid #afafaf;
  background: ${({ theme }) => theme.palette.brand.backgroundSidebar};
  overflow: visible;
`;

const YearBandsContainer = styled.div`
  position: relative;
  width: 100%;
  height: 53px;
`;

const YearBand = styled.div<{ $isEven: boolean; left: number; width: number }>`
  position: absolute;
  left: ${({ left }) => left}%;
  width: ${({ width }) => width}%;
  height: 100%;
  background-color: ${({ theme }) => theme.palette.brand.backgroundSidebar};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const YearLabel = styled.div`
  font-size: 16px;
  font-weight: 900;
  color: ${({ theme }) => theme.palette.brand.primary};
`;

const MonthIndicatorsContainer = styled.div`
  position: relative;
  width: 100%;
  height: 27px;
`;

const MonthIndicator = styled.div<{ centerPosition: number }>`
  position: absolute;
  left: ${({ centerPosition }) => centerPosition}%;
  transform: translateX(-50%);
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.brand.primary};
`;

const MonthBoundaryLines = styled.div`
  position: absolute;
  top: 53px; /* Start after year section */
  left: 0;
  right: 0;
  bottom: 0; /* Go to the horizontal line */
  pointer-events: none;
`;

const MonthBoundaryLine = styled.div<{ left: number }>`
  position: absolute;
  left: ${({ left }) => left}%;
  top: 0;
  bottom: 0;
  width: 1px;
  background-color: #afafaf;
`;

const YearBoundaryLines = styled.div`
  position: absolute;
  top: 0; /* Start at the very top of the timeline container */
  left: 0;
  right: 0;
  bottom: -50px; /* Extend 50px below to cover the timeline area */
  pointer-events: none;
`;

const YearBoundaryLine = styled.div<{ left: number }>`
  position: absolute;
  left: ${({ left }) => left}%;
  top: 0;
  bottom: -50px; /* Extend to match parent container */
  width: 2.25px;
  background-image: linear-gradient(#afafaf 33%, rgba(255, 255, 255, 0) 0%);
  background-size: 2.25px 12px;
  background-repeat: repeat-y;
`;

const TodayTriangle = styled.div<{ left: number }>`
  position: absolute;
  left: ${({ left }) => `calc(${left}% - 11px)`};
  bottom: -52.5px; /* Moves triangle point further down */
  width: 0;
  height: 0;
  border-left: 12px solid transparent;
  border-right: 12px solid transparent;
  border-top: 16px solid ${({ theme }) => theme.palette.brand.primary};
  z-index: 21;
  pointer-events: none;
`;

export { GanttTimelineHeader };
